import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Card, Row, Col, Button, Spinner } from 'react-bootstrap';
import { Formik, Field, Form } from 'formik';
import type { FormikProps } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';

import infoIco from 'assets/images/i-circle.svg';
import Verified from 'assets/images/verified.gif';

import UserHttp from '../services/profile.service';
import { netcoreTrack, netcoreDate } from 'utils/netcoreTracking';
import { useCooldownTimer } from '../hooks/useCooldownTimer';
import ConfirmBankDetailsModal from './ConfirmBankDetailsModal';
import ConfirmBankDocumentModal from './ConfirmBankDocumentModal';

/* ───────────────────────── Types ───────────────────────── */

interface BankDetails {
  accountNo?: string;
  ifsc?: string;
  name?: string;
  bank?: string;
  state?: 'VERIFIED' | 'PENDING' | 'REJECTED' | string;
  bankStatus?: 'BANK_REJECTED' | 'NAME_MISMATCH' | string;
  rejectedByAdmin?: boolean;
  consent?: boolean;
}

interface BankVerification {
  active?: boolean;
  attachmentUrl?: string;
  message?: string;
  uploadDate?: string;
}

interface KycData {
  pan?: {
    isVerified?: boolean;
    data?: {
      name?: string;
    };
  };
}

interface Customer {
  customerId: string;
  bankDetails?: BankDetails;
  bankDetailsAddCount?: number;
  bankVerification?: BankVerification;
  kyc?: KycData;
}

interface User {
  userId?: string;
  mobile?: { value?: string };
}

interface BankFormProps {
  customer: Customer;
  user: User | null;
  setCustomer: (customer: Customer) => void;
}

interface BankFormValues {
  bankAccount: string;
  confirmBankAccount: string;
  nameAsOnBankAccount: string;
  ifsc: string;
  bankName: string;
}

type BankUIAction =
  | 'SUBMIT'
  | 'EDIT'
  | 'UPLOAD_DOC'
  | 'PENDING'
  | 'VERIFIED'
  | 'LOCKED'
  | 'NONE';

/* ───────────────────────── Constants ───────────────────────── */

const IFSC_REGEX = /^[A-Z]{4}0[A-Z0-9]{6}$/;
const ACCOUNT_REGEX = /^\d{9,18}$/;
const ALPHANUMERIC_UPPER_REGEX = /^[A-Z0-9]*$/;
const VERIFICATION_COOLDOWN_MS = 60_000;
const MAX_ACCOUNT_LENGTH = 18;
const MAX_IFSC_LENGTH = 11;
const MAX_FILE_SIZE_BYTES = 3 * 1024 * 1024;
const IFSC_API_BASE = 'https://ifsc.razorpay.com';

const BUTTON_COLOR_MAP: Record<string, React.CSSProperties> = {
  primary: {
    backgroundColor: '#99834D',
    borderColor: '#99834D',
    color: '#ffffff',
  },
  warning: {
    backgroundColor: '#F0AD4E',
    borderColor: '#EEA236',
    color: '#ffffff',
  },
  success: {
    backgroundColor: '#28A745',
    borderColor: '#218838',
    color: '#ffffff',
  },
  info: {
    backgroundColor: '#99834D',
    borderColor: '#99834D',
    color: '#ffffff',
  },
  secondary: {
    backgroundColor: '#E9ECEF',
    borderColor: '#CED4DA',
    color: '#495057',
  },
  dark: {
    backgroundColor: '#343A40',
    borderColor: '#343A40',
    color: '#ffffff',
  },
} as const;

/* ───────────────────────── Validation Schema ───────────────────────── */

const bankFormSchema = Yup.object().shape({
  bankAccount: Yup.string()
    .matches(ACCOUNT_REGEX, 'Account number must be 9–18 digits')
    .required('Account number is required'),
  confirmBankAccount: Yup.string()
    .matches(ACCOUNT_REGEX, 'Account number must be 9–18 digits')
    .required('Please confirm your account number')
    .oneOf([Yup.ref('bankAccount')], 'Account numbers do not match'),
  nameAsOnBankAccount: Yup.string().required('Name is required'),
  ifsc: Yup.string()
    .matches(IFSC_REGEX, 'Invalid IFSC format')
    .required('IFSC code is required'),
  bankName: Yup.string().required('Bank name is required'),
});

/* ───────────────────────── Helpers ───────────────────────── */

const primaryBtnStyle = (variant: string = 'primary'): React.CSSProperties => ({
  height: 42,
  minWidth: 180,
  padding: '0 20px',
  borderRadius: 6,
  fontWeight: 600,
  fontSize: '14px',
  letterSpacing: '0.4px',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderWidth: 1,
  borderStyle: 'solid',
  cursor: 'pointer',
  transition: 'all 0.2s ease-in-out',
  ...(BUTTON_COLOR_MAP[variant] ?? BUTTON_COLOR_MAP.primary),
});

const wait = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

const getErrorMessage = (error: string | string[] | undefined): string => {
  if (!error) return '';
  if (typeof error === 'string') return error;
  if (Array.isArray(error)) return error[0] ?? '';
  return String(error);
};

const sanitizeBankDetails = (values: BankFormValues) => ({
  name: values.nameAsOnBankAccount.trim().toUpperCase(),
  accountNo: values.bankAccount.replace(/\D/g, '').trim(),
  ifsc: values.ifsc.trim().toUpperCase(),
  bank: values.bankName,
});

const getBankUIAction = (customer: Customer): BankUIAction => {
  const bank = customer?.bankDetails ?? {};
  const attemptsLeft = customer?.bankDetailsAddCount ?? 0;
  const bankVerification = customer?.bankVerification;

  const hasNoBankDetails = !bank.accountNo && !bank.ifsc && !bank.name;

  if (hasNoBankDetails) return 'SUBMIT';
  if (bank.state === 'VERIFIED') return 'VERIFIED';

  if (
    bank.state === 'REJECTED' &&
    bank.bankStatus === 'NAME_MISMATCH' &&
    !bank.rejectedByAdmin
  ) {
    return 'NONE';
  }

  if (attemptsLeft === 0 && bankVerification?.active === false)
    return 'UPLOAD_DOC';

  if (bankVerification?.attachmentUrl) return 'PENDING';

  if (attemptsLeft > 0 && bank.state !== 'VERIFIED') return 'EDIT';

  if (attemptsLeft === 0 && !bankVerification?.attachmentUrl)
    return 'UPLOAD_DOC';

  return 'PENDING';
};

/* ───────────────────────── Component ───────────────────────── */

const BankForm: React.FC<BankFormProps> = ({ customer, user, setCustomer }) => {
  const formikRef = useRef<FormikProps<BankFormValues>>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const isMountedRef = useRef(true);

  const [isIfscVerified, setIsIfscVerified] = useState(false);
  const [isBankSearching, setIsBankSearching] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isVerifiedAndSaved, setIsVerifiedAndSaved] = useState(false);
  const [ifscApiError, setIfscApiError] = useState<string | null>(null);

  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const [bankProofFile, setBankProofFile] = useState<File | null>(null);
  const [bankProofPreview, setBankProofPreview] = useState<string | null>(null);

  const [showUploadModal, setShowUploadModal] = useState(false);

  const existingBankDetails = customer?.bankDetails ?? {};

  const [ifscValue, setIfscValue] = useState(existingBankDetails.ifsc ?? '');

  const initialValues: BankFormValues = {
    bankAccount: existingBankDetails.accountNo ?? '',
    confirmBankAccount: '',
    nameAsOnBankAccount:
      existingBankDetails.name ?? customer?.kyc?.pan?.data?.name ?? '',
    ifsc: existingBankDetails.ifsc ?? '',
    bankName: existingBankDetails.bank ?? '',
  };

  const {
    timeLeft,
    isActive: bankPending,
    start: startCooldownTimer,
  } = useCooldownTimer('bankVerificationTimerEnd');

  const uiAction = getBankUIAction(customer);
  const areFieldsEditable = uiAction === 'SUBMIT' || uiAction === 'EDIT';
  const isUiLocked = isProcessing || isBankSearching || isVerifiedAndSaved;
  const canEditFields = areFieldsEditable && !isUiLocked;

  const requiresConsent =
    uiAction === 'SUBMIT' && !customer?.bankDetails?.consent;

  // Track mount status for async safety
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Cleanup abort controller on unmount
  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    if (existingBankDetails?.ifsc) {
      setIsIfscVerified(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!bankPending && timeLeft === 0) {
      UserHttp.getProfile(customer.customerId, user?.userId)
        .then((res) => {
          if (!isMountedRef.current) return;
          const updated = res?.data?.data?.customer;
          if (updated) setCustomer(updated);
        })
        .catch(() => {
          /* silently ignore — profile refresh is best-effort */
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bankPending, timeLeft]);

  // Revoke object URL on preview change to prevent memory leaks
  useEffect(() => {
    return () => {
      if (bankProofPreview?.startsWith('blob:')) {
        URL.revokeObjectURL(bankProofPreview);
      }
    };
  }, [bankProofPreview]);

  const handleBankProofSelect = useCallback((file: File) => {
    if (!file.type.startsWith('image/')) {
      toast.error('Only image files are allowed');
      return;
    }

    setBankProofFile(file);

    if (['image/heic', 'image/heif'].includes(file.type)) {
      setBankProofPreview(null);
      toast.info('Preview not available for iPhone images.');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      if (isMountedRef.current) {
        setBankProofPreview(reader.result as string);
      }
    };
    reader.onerror = () => {
      if (isMountedRef.current) {
        toast.error('Failed to read file');
        setBankProofPreview(null);
      }
    };
    reader.readAsDataURL(file);
  }, []);

  const verifyIfscCode = useCallback(
    async (
      ifsc: string,
      setFieldValue: (field: string, value: string) => void,
      setErrors: (errors: Record<string, string>) => void
    ) => {
      if (!ifsc || isUiLocked) return;

      // Abort any in-flight IFSC request
      abortControllerRef.current?.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;

      try {
        setIsBankSearching(true);
        setIfscApiError(null);

        const res = await fetch(`${IFSC_API_BASE}/${encodeURIComponent(ifsc)}`, {
          signal: controller.signal,
        });

        if (!res.ok) throw new Error(`IFSC lookup failed: ${res.status}`);

        const data = await res.json();
        if (!data?.BANK) throw new Error('No bank data returned');

        if (!isMountedRef.current) return;

        setIsIfscVerified(true);
        setFieldValue('bankName', data.BANK);
      } catch (err: unknown) {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        if (!isMountedRef.current) return;

        setIsIfscVerified(false);
        setFieldValue('bankName', '');
        setErrors({ ifsc: 'Invalid IFSC code' });
        setIfscApiError('Invalid IFSC code');
        toast.error('Invalid IFSC code');
      } finally {
        if (isMountedRef.current) {
          setIsBankSearching(false);
        }
      }
    },
    [isUiLocked]
  );

  const handleVerify = useCallback(
    async (
      values: BankFormValues,
      setErrors: (errors: Record<string, string>) => void
    ) => {
      if (isUiLocked) return;

      setIsProcessing(true);
      const start = Date.now();

      try {
        const sanitized = sanitizeBankDetails(values);

        const payload = {
          transactionType: 'VERIFICATION' as const,
          transactionMethod: 'IMPS' as const,
          referenceId: customer.customerId,
          referenceType: 'CUSTOMER' as const,
          customerAccount: {
            accountType: 'BANK' as const,
            ...sanitized,
            ...(requiresConsent && { consent: true }),
          },
        };

        const res = await UserHttp.accountVerify(payload, user?.userId);
        if (res.status !== 200) throw new Error(`Verification returned ${res.status}`);

        const profileRes = await UserHttp.getProfile(
          customer.customerId,
          user?.userId
        );

        if (!isMountedRef.current) return;

        const updatedCustomer = profileRes?.data?.data?.customer;
        const bank = updatedCustomer?.bankDetails;

        if (updatedCustomer) setCustomer(updatedCustomer);

        // Netcore: status may still be pending here; backend does the final verification.
        const bankStatus =
          bank?.state === 'VERIFIED' || bank?.state === 'REJECTED'
            ? bank.state
            : 'VERIFICATION_PENDING';
        netcoreTrack(
          'bank_details_add',
          {
            customer_id: customer.customerId,
            bank_name: sanitized.bank,
            verification_status: bankStatus,
            new_added_at: netcoreDate(new Date()),
          },
          user?.mobile?.value,
          {
            BANK_NAME: sanitized.bank,
            BANK_VERIFICATION_STATUS: bankStatus,
          }
        );

        if (bank?.state === 'VERIFIED') {
          setIsVerifiedAndSaved(true);
          toast.success('Bank verified successfully.');
        } else {
          startCooldownTimer(VERIFICATION_COOLDOWN_MS);
          toast.info('Verification in progress.');
        }
      } catch {
        if (isMountedRef.current) {
          toast.error('Verification failed');
        }
      } finally {
        const elapsed = Date.now() - start;
        await wait(Math.max(0, VERIFICATION_COOLDOWN_MS - elapsed));
        if (isMountedRef.current) {
          setIsProcessing(false);
        }
      }
    },
    [
      isUiLocked,
      customer.customerId,
      requiresConsent,
      user?.userId,
      user?.mobile?.value,
      setCustomer,
      startCooldownTimer,
    ]
  );

  const handleSubmit = useCallback(
    async (
      values: BankFormValues,
      { setErrors }: { setErrors: (errors: Record<string, string>) => void }
    ) => {
      if (!customer?.kyc?.pan?.isVerified) {
        toast.warning('Please verify PAN first.');
        return;
      }

      if (values.bankAccount !== values.confirmBankAccount) {
        toast.error('Account numbers do not match.');
        return;
      }

      await handleVerify(values, setErrors);
    },
    [customer?.kyc?.pan?.isVerified, handleVerify]
  );

  const handleUploadBankProof = useCallback(async () => {
    if (!bankProofFile) return;

    const formData = new FormData();
    formData.append('file', bankProofFile);

    try {
      setIsProcessing(true);
      await UserHttp.uploadBankVerificationDoc(formData);

      if (!isMountedRef.current) return;
      toast.success('Bank proof uploaded successfully');

      const profileRes = await UserHttp.getProfile(
        customer.customerId,
        user?.userId
      );

      if (!isMountedRef.current) return;

      const updatedCustomer = profileRes?.data?.data?.customer;
      if (updatedCustomer) setCustomer(updatedCustomer);

      setBankProofFile(null);
      setBankProofPreview(null);
      setShowUploadModal(false);
    } catch {
      if (isMountedRef.current) {
        toast.error('Failed to upload bank proof');
      }
    } finally {
      if (isMountedRef.current) {
        setIsProcessing(false);
      }
    }
  }, [bankProofFile, customer.customerId, user?.userId, setCustomer]);

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (isUiLocked) return;
      const file = e.target.files?.[0];
      // Reset input so the same file can be re-selected
      e.target.value = '';
      if (!file) return;
      if (file.size > MAX_FILE_SIZE_BYTES) {
        toast.error('File size should not exceed 3MB');
        return;
      }
      handleBankProofSelect(file);
    },
    [isUiLocked, handleBankProofSelect]
  );

  const handleRemoveProof = useCallback(() => {
    setBankProofFile(null);
    setBankProofPreview(null);
  }, []);

  const openConfirmModal = useCallback(() => setShowConfirmModal(true), []);
  const closeConfirmModal = useCallback(() => setShowConfirmModal(false), []);
  const openUploadModal = useCallback(() => setShowUploadModal(true), []);
  const closeUploadModal = useCallback(() => setShowUploadModal(false), []);

  const handleConfirmAndSubmit = useCallback(() => {
    setShowConfirmModal(false);
    formikRef.current?.submitForm();
  }, []);

  /* ───────────────────────── JSX ───────────────────────── */

  return (
    <>
      <Card className='mt-5 shadow-sm'>
        <Card.Body className='p-4 p-md-5'>
          <Card.Title className='mb-4 fw-semibold fs-4'>
            Bank Details
          </Card.Title>

          <Formik<BankFormValues>
            innerRef={formikRef}
            enableReinitialize
            initialValues={initialValues}
            validationSchema={bankFormSchema}
            onSubmit={handleSubmit}
          >
            {({
              errors,
              touched,
              values,
              isValid,
              setFieldValue,
              setErrors,
            }) => {
              const isAccountValid = ACCOUNT_REGEX.test(values.bankAccount);
              const isIfscFormatValid = IFSC_REGEX.test(values.ifsc);

              const canSubmit =
                (uiAction === 'SUBMIT' || uiAction === 'EDIT') &&
                isAccountValid &&
                isIfscFormatValid &&
                isIfscVerified &&
                (values.bankName?.length ?? 0) > 0 &&
                values.confirmBankAccount === values.bankAccount &&
                isValid &&
                !isProcessing &&
                !isVerifiedAndSaved &&
                !bankPending;

              const getPrimaryButtonConfig = () => {
                switch (uiAction) {
                  case 'NONE':
                    return null;

                  case 'SUBMIT':
                    return {
                      label: 'Submit Bank Details',
                      variant: 'primary',
                      disabled: !canSubmit,
                      onClick: openConfirmModal,
                    };

                  case 'EDIT':
                    return {
                      label: 'Edit & Resubmit',
                      variant: 'warning',
                      disabled: !canSubmit,
                      onClick: openConfirmModal,
                    };

                  case 'UPLOAD_DOC':
                    return {
                      label: 'Upload Bank Proof',
                      variant: 'info',
                      disabled: !bankProofFile || isProcessing,
                      onClick: openUploadModal,
                    };

                  case 'PENDING':
                    return {
                      label: isProcessing
                        ? 'Processing…'
                        : 'Verification in progress…',
                      variant: 'info',
                      disabled: true,
                      onClick: undefined,
                    };

                  case 'VERIFIED':
                    return {
                      label: 'Bank Verified',
                      variant: 'success',
                      disabled: true,
                      onClick: undefined,
                    };

                  case 'LOCKED':
                    return {
                      label: 'Contact Support',
                      variant: 'dark',
                      disabled: true,
                      onClick: undefined,
                    };

                  default:
                    return null;
                }
              };

              const buttonConfig = getPrimaryButtonConfig();

              const bank = customer?.bankDetails ?? {};
              const attemptsLeft = customer?.bankDetailsAddCount ?? 0;
              const bankVerification = customer?.bankVerification;

              const showBankRejectedMessage =
                (uiAction === 'EDIT' || uiAction === 'PENDING') &&
                attemptsLeft > 0 &&
                (bank.state === 'PENDING' ||
                  (bank.state === 'REJECTED' &&
                    bank.bankStatus === 'BANK_REJECTED') ||
                  (bank.state === 'REJECTED' &&
                    bank.bankStatus === 'NAME_MISMATCH' &&
                    bank.rejectedByAdmin === true));

              const showNameMismatchMessage =
                uiAction === 'NONE' &&
                bank.state === 'REJECTED' &&
                bank.bankStatus === 'NAME_MISMATCH' &&
                bank.rejectedByAdmin === false;

              const showBankVerificationMessage =
                uiAction === 'UPLOAD_DOC' && attemptsLeft === 0;

              const fallbackUploadMessage =
                'All attempts have been used. Please upload your bank document (cancelled cheque or bank passbook or statement) to complete verification.';

              return (
                <Form
                  style={{
                    pointerEvents: isUiLocked ? 'none' : 'auto',
                    opacity: isUiLocked ? 0.7 : 1,
                  }}
                >
                  {/* ── Rejected banner ── */}
                  {showBankRejectedMessage && (
                    <div
                      className='mb-4 p-4 rounded-3'
                      style={{
                        background: '#FFF4F4',
                        border: '1px solid #F5C2C7',
                        borderLeft: '4px solid #DC3545',
                      }}
                    >
                      <div className='d-flex align-items-start gap-3'>
                        <div style={{ width: 20, height: 20, display: 'flex' }}>
                          <svg width='20' height='20' viewBox='0 0 24 24' fill='none' aria-hidden='true'>
                            <path d='M12 9V13' stroke='#DC2626' strokeWidth='2' strokeLinecap='round' />
                            <circle cx='12' cy='17' r='1' fill='#DC2626' />
                            <path
                              d='M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z'
                              stroke='#DC2626' strokeWidth='2' strokeLinejoin='round'
                            />
                          </svg>
                        </div>
                        <div>
                          <div className='mb-2' style={{ fontSize: 14, color: '#DC3545' }}>
                            Your bank did not accept the submitted account details. Please review
                            and correct the information before resubmitting.
                          </div>
                          <div
                            className='fw-semibold'
                            style={{ color: attemptsLeft <= 1 ? '#DC3545' : '#856404', fontSize: 14 }}
                          >
                            {attemptsLeft} attempt{attemptsLeft !== 1 && 's'} remaining
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* ── Name mismatch banner ── */}
                  {showNameMismatchMessage && (
                    <div
                      className='mb-4 p-4 rounded-3'
                      style={{
                        background: '#FFF9E6',
                        border: '1px solid #FFE69C',
                        borderLeft: '4px solid #F0AD4E',
                      }}
                    >
                      <div className='d-flex align-items-start gap-3'>
                        <div style={{ width: 20, height: 20, display: 'flex' }}>
                          <svg width='20' height='20' viewBox='0 0 24 24' fill='none' aria-hidden='true'>
                            <circle cx='12' cy='12' r='10' stroke='#F0AD4E' strokeWidth='2' />
                            <path d='M12 10V16' stroke='#F0AD4E' strokeWidth='2' strokeLinecap='round' />
                            <circle cx='12' cy='7' r='1' fill='#F0AD4E' />
                          </svg>
                        </div>
                        <div>
                          <div className='text-muted' style={{ fontSize: 14 }}>
                            The account holder name does not match our records. Our team is
                            reviewing this and will update you shortly.
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* ── Upload doc banner ── */}
                  {showBankVerificationMessage && (
                    <div
                      className='mb-4 p-4 rounded-3'
                      style={{
                        background: '#FFF4F4',
                        border: '1px solid #F5C2C7',
                        borderLeft: '4px solid #DC3545',
                      }}
                    >
                      <div className='d-flex align-items-start gap-3'>
                        <div style={{ width: 20, height: 20, display: 'flex' }}>
                          <svg width='20' height='20' viewBox='0 0 24 24' fill='none' aria-hidden='true'>
                            <path d='M12 9V13' stroke='#DC2626' strokeWidth='2' strokeLinecap='round' />
                            <circle cx='12' cy='17' r='1' fill='#DC2626' />
                            <path
                              d='M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z'
                              stroke='#DC2626' strokeWidth='2' strokeLinejoin='round'
                            />
                          </svg>
                        </div>
                        <div>
                          <div className='mb-2' style={{ fontSize: 14, color: '#DC2626' }}>
                            {bankVerification?.message ?? fallbackUploadMessage}
                          </div>
                          {bankVerification?.uploadDate && (
                            <div className='small text-muted mb-2' style={{ fontSize: 12 }}>
                              Uploaded on:{' '}
                              {new Date(bankVerification.uploadDate).toLocaleDateString()}
                            </div>
                          )}
                          {bankVerification?.attachmentUrl && (
                            <div className='mt-2'>
                              <Button
                                size='sm'
                                variant='outline-danger'
                                onClick={() =>
                                  window.open(bankVerification.attachmentUrl, '_blank', 'noopener,noreferrer')
                                }
                              >
                                View Uploaded Document
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* ── Fields ── */}
                  <Row>
                    {/* Customer Name */}
                    <Col md={bank?.state === 'VERIFIED' ? 6 : 12}>
                      <div className='form-group mb-3'>
                        <label className='form-label' htmlFor='nameAsOnBankAccount'>
                          Customer Name
                        </label>
                        <Field
                          id='nameAsOnBankAccount'
                          type='text'
                          name='nameAsOnBankAccount'
                          disabled
                          className='form-control'
                        />
                        {errors.nameAsOnBankAccount && touched.nameAsOnBankAccount && (
                          <small>
                            <img src={infoIco} alt='' />{' '}
                            {getErrorMessage(errors.nameAsOnBankAccount)}
                          </small>
                        )}
                      </div>
                    </Col>

                    {/* Account Number */}
                    <Col md={6}>
                      <div className='form-group mb-3'>
                        <label className='form-label' htmlFor='bankAccount'>
                          Account Number
                        </label>

                        <div style={{ position: 'relative' }}>
                          <Field
                            id='bankAccount'
                            type={uiAction === 'VERIFIED' ? 'text' : 'password'}
                            name='bankAccount'
                            disabled={!canEditFields || !!values.confirmBankAccount}
                            className='form-control'
                            inputMode='numeric'
                            pattern='[0-9]*'
                            value={
                              uiAction === 'VERIFIED'
                                ? values.bankAccount
                                  .slice(0, -4)
                                  .replace(/./g, '•') + values.bankAccount.slice(-4)
                                : undefined
                            }
                            style={{
                              paddingRight:
                                customer?.bankDetails?.state === 'PENDING' ||
                                  customer?.bankDetails?.state === 'REJECTED'
                                  ? '130px'
                                  : undefined,
                              letterSpacing: uiAction === 'VERIFIED' ? '2px' : undefined,
                            }}
                            autoComplete='off'
                            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                              // Allow: backspace, delete, tab, escape, enter, arrows, home, end
                              const allowedKeys = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter',
                                'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
                              if (allowedKeys.includes(e.key)) return;
                              // Allow Ctrl/Cmd + A, C, V, X (select all, copy, paste, cut)
                              if ((e.ctrlKey || e.metaKey) && ['a', 'c', 'v', 'x'].includes(e.key.toLowerCase())) return;
                              // Block anything that isn't a digit
                              if (!/^\d$/.test(e.key)) {
                                e.preventDefault();
                              }
                            }}
                            onPaste={(e: React.ClipboardEvent<HTMLInputElement>) => {
                              const pasted = e.clipboardData.getData('text');
                              if (/\D/.test(pasted)) {
                                e.preventDefault();
                                const digitsOnly = pasted.replace(/\D/g, '');
                                if (digitsOnly) {
                                  const current = values.bankAccount;
                                  const combined = (current + digitsOnly).slice(0, MAX_ACCOUNT_LENGTH);
                                  setFieldValue('bankAccount', combined);
                                }
                              }
                            }}
                            onDrop={(e: React.DragEvent<HTMLInputElement>) => {
                              e.preventDefault();
                            }}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                              if (isUiLocked) return;
                              const val = e.target.value.replace(/\D/g, '').slice(0, MAX_ACCOUNT_LENGTH);
                              setFieldValue('bankAccount', val);
                            }}
                          />

                          {(customer?.bankDetails?.state === 'PENDING' ||
                            customer?.bankDetails?.state === 'REJECTED') && (
                              <span
                                style={{
                                  position: 'absolute',
                                  right: 0,
                                  top: 0,
                                  height: '100%',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  padding: '0 16px',
                                  fontSize: '11px',
                                  fontWeight: 600,
                                  letterSpacing: '0.6px',
                                  textTransform: 'uppercase',
                                  borderTopRightRadius: '4px',
                                  borderBottomRightRadius: '4px',
                                  pointerEvents: 'none',
                                  zIndex: 2,
                                  color: '#fde2e2',
                                  backgroundColor: '#ba2f3dff',
                                  borderLeft: '1px solid #ffeeba',
                                }}
                                aria-label='Unverified account'
                              >
                                UNVERIFIED
                              </span>
                            )}
                        </div>

                        {errors.bankAccount && touched.bankAccount && (
                          <small role='alert'>
                            <img src={infoIco} alt='' />{' '}
                            {getErrorMessage(errors.bankAccount)}
                          </small>
                        )}
                      </div>
                    </Col>

                    {/* Confirm Account Number */}
                    {bank?.state !== 'VERIFIED' && (
                      <Col md={6}>
                        <div className='form-group mb-3'>
                          <label className='form-label' htmlFor='confirmBankAccount'>
                            Confirm Account Number
                          </label>

                          <div style={{ position: 'relative' }}>
                            <Field
                              id='confirmBankAccount'
                              type='text'
                              name='confirmBankAccount'
                              disabled={
                                !canEditFields ||
                                !values.bankAccount ||
                                bank.state === 'VERIFIED'
                              }
                              className='form-control'
                              inputMode='numeric'
                              pattern='[0-9]*'
                              value={
                                uiAction === 'VERIFIED'
                                  ? values.bankAccount
                                    .slice(0, -4)
                                    .replace(/./g, '•') + values.bankAccount.slice(-4)
                                  : undefined
                              }
                              style={{
                                letterSpacing:
                                  uiAction === 'VERIFIED' ? '2px' : undefined,
                              }}
                              autoComplete='off'
                              onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                                const allowedKeys = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter',
                                  'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
                                if (allowedKeys.includes(e.key)) return;
                                if ((e.ctrlKey || e.metaKey) && ['a', 'c', 'v', 'x'].includes(e.key.toLowerCase())) return;
                                if (!/^\d$/.test(e.key)) {
                                  e.preventDefault();
                                }
                              }}
                              onPaste={(e: React.ClipboardEvent) => e.preventDefault()}
                              onCopy={(e: React.ClipboardEvent) => e.preventDefault()}
                              onCut={(e: React.ClipboardEvent) => e.preventDefault()}
                              onDrop={(e: React.DragEvent<HTMLInputElement>) => {
                                e.preventDefault();
                              }}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                if (isUiLocked) return;
                                const val = e.target.value
                                  .replace(/\D/g, '')
                                  .slice(0, MAX_ACCOUNT_LENGTH);
                                setFieldValue('confirmBankAccount', val);
                              }}
                            />
                          </div>

                          {uiAction !== 'VERIFIED' &&
                            errors.confirmBankAccount &&
                            touched.confirmBankAccount && (
                              <small role='alert'>
                                <img src={infoIco} alt='' />{' '}
                                {getErrorMessage(errors.confirmBankAccount)}
                              </small>
                            )}

                          {uiAction !== 'VERIFIED' &&
                            !errors.confirmBankAccount &&
                            touched.confirmBankAccount &&
                            values.confirmBankAccount &&
                            values.bankAccount === values.confirmBankAccount && (
                              <small
                                className='d-flex align-items-center gap-1 mt-1'
                                style={{ color: '#198754' }}
                              >
                                <svg
                                  xmlns='http://www.w3.org/2000/svg'
                                  width='14'
                                  height='14'
                                  viewBox='0 0 24 24'
                                  fill='none'
                                  stroke='#198754'
                                  strokeWidth='2.5'
                                  strokeLinecap='round'
                                  strokeLinejoin='round'
                                  aria-hidden='true'
                                >
                                  <circle cx='12' cy='12' r='10' />
                                  <path d='M8 12.5l2.5 2.5L16 9.5' />
                                </svg>
                                Account numbers match
                              </small>
                            )}
                        </div>
                      </Col>
                    )}
                  </Row>

                  <Row>
                    {/* IFSC Code */}
                    <Col md={6}>
                      <div className='form-group mb-3 position-relative'>
                        <label className='form-label fw-semibold text-dark' htmlFor='ifsc'>
                          IFSC Code
                        </label>

                        <div className='input-group'>
                          <Field name='ifsc'>
                            {({ field }: { field: Record<string, unknown> }) => (
                              <input
                                {...field}
                                id='ifsc'
                                type='text'
                                disabled={!canEditFields}
                                placeholder='Enter IFSC Code'
                                className='form-control'
                                value={ifscValue}
                                autoComplete='off'
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                  if (isUiLocked) return;
                                  const input = e.target.value.toUpperCase();
                                  if (!ALPHANUMERIC_UPPER_REGEX.test(input)) return;
                                  if (input.length > MAX_IFSC_LENGTH) return;

                                  setIfscValue(input);
                                  setFieldValue('ifsc', input);
                                  setFieldValue('bankName', '');
                                  setIsIfscVerified(false);
                                  setIfscApiError(null);
                                }}
                              />
                            )}
                          </Field>

                          <div
                            className='d-flex align-items-center bg-white border border-1 rounded-end'
                            style={{ borderColor: '#ced4da', borderLeft: 'none' }}
                          >
                            {isIfscVerified ? (
                              <div className='px-2 d-flex align-items-center'>
                                <img src={Verified} width='24' height='24' alt='Verified' />
                              </div>
                            ) : (
                              areFieldsEditable &&
                              !isIfscVerified && (
                                <Button
                                  type='button'
                                  className='btn fw-semibold'
                                  onClick={() =>
                                    verifyIfscCode(ifscValue, setFieldValue, setErrors)
                                  }
                                  disabled={
                                    isUiLocked || !IFSC_REGEX.test(ifscValue)
                                  }
                                  style={{
                                    backgroundColor: isBankSearching
                                      ? '#e9e9e9'
                                      : '#99834D',
                                    color: '#fff',
                                    border: '1px solid #ced4da',
                                    borderLeft: 'none',
                                    borderRadius: '0 0.375rem 0.375rem 0',
                                    height: 'calc(2.25rem + 2px)',
                                    padding: '0 1rem',
                                  }}
                                  aria-label='Search IFSC code'
                                >
                                  {isBankSearching ? (
                                    <Spinner animation='border' size='sm' />
                                  ) : (
                                    'Search'
                                  )}
                                </Button>
                              )
                            )}
                          </div>
                        </div>

                        {!isIfscVerified && ifscValue && (
                          <>
                            <ul
                              className='small mt-2 mb-1 ps-3'
                              style={{ listStyle: 'none' }}
                              aria-label='IFSC validation rules'
                            >
                              {[
                                {
                                  text: 'First 4 characters must be alphabets (A–Z)',
                                  valid: /^[A-Z]{4}/.test(ifscValue),
                                },
                                {
                                  text: '5th character must be the number 0',
                                  valid:
                                    ifscValue.length >= 5 && ifscValue[4] === '0',
                                },
                                {
                                  text: 'IFSC code must be exactly 11 characters long',
                                  valid: ifscValue.length === 11,
                                },
                              ].map((rule, index) => (
                                <li
                                  key={index}
                                  className='d-flex align-items-center mb-2'
                                  style={{
                                    color: rule.valid ? '#198754' : '#dc3545',
                                    fontWeight: 500,
                                  }}
                                >
                                  {rule.valid ? (
                                    <svg
                                      xmlns='http://www.w3.org/2000/svg'
                                      width='16'
                                      height='16'
                                      viewBox='0 0 24 24'
                                      fill='none'
                                      stroke='#198754'
                                      strokeWidth='2'
                                      strokeLinecap='round'
                                      strokeLinejoin='round'
                                      className='me-2'
                                      aria-hidden='true'
                                    >
                                      <circle
                                        cx='12'
                                        cy='12'
                                        r='10'
                                        stroke='#198754'
                                        fill='none'
                                      />
                                      <path
                                        d='M8 12.5l2.5 2.5L16 9.5'
                                        stroke='#198754'
                                      />
                                    </svg>
                                  ) : (
                                    <svg
                                      xmlns='http://www.w3.org/2000/svg'
                                      width='16'
                                      height='16'
                                      viewBox='0 0 24 24'
                                      fill='none'
                                      stroke='#dc3545'
                                      strokeWidth='2'
                                      strokeLinecap='round'
                                      strokeLinejoin='round'
                                      className='me-2'
                                      aria-hidden='true'
                                    >
                                      <circle
                                        cx='12'
                                        cy='12'
                                        r='10'
                                        stroke='#dc3545'
                                        fill='none'
                                      />
                                      <line
                                        x1='15'
                                        y1='9'
                                        x2='9'
                                        y2='15'
                                        stroke='#dc3545'
                                      />
                                      <line
                                        x1='9'
                                        y1='9'
                                        x2='15'
                                        y2='15'
                                        stroke='#dc3545'
                                      />
                                    </svg>
                                  )}
                                  {rule.text}
                                </li>
                              ))}

                              {ifscApiError && (
                                <li
                                  className='d-flex align-items-center'
                                  style={{ color: '#dc3545' }}
                                  role='alert'
                                >
                                  {ifscApiError}
                                </li>
                              )}
                            </ul>

                            <div className='mt-2'>
                              <a
                                href='https://www.rbi.org.in/Scripts/IFSCMICRDetails.aspx'
                                target='_blank'
                                rel='noopener noreferrer'
                                className='text-primary small'
                              >
                                Search for your IFSC code here
                              </a>
                            </div>
                          </>
                        )}
                      </div>
                    </Col>

                    {/* Bank Name */}
                    <Col md={6}>
                      <div className='form-group mb-3'>
                        <label className='form-label' htmlFor='bankName'>
                          Bank Name
                        </label>
                        <Field
                          id='bankName'
                          type='text'
                          name='bankName'
                          readOnly
                          className='form-control'
                          disabled
                        />
                      </div>
                    </Col>
                  </Row>

                  {/* Upload doc section */}
                  {uiAction === 'UPLOAD_DOC' && (
                    <div className='mt-3'>
                      <label className='form-label fw-semibold small mb-2'>
                        Upload Bank Proof
                        <span className='text-muted small ms-1'>
                          (Cancelled cheque / Bank statement)
                        </span>
                      </label>

                      {!bankProofFile && (
                        <div
                          className='border rounded p-3 text-center'
                          role='button'
                          tabIndex={0}
                          aria-label='Click to upload bank proof'
                          style={{
                            cursor: isUiLocked ? 'not-allowed' : 'pointer',
                            opacity: isUiLocked ? 0.6 : 1,
                          }}
                          onClick={() => {
                            if (isUiLocked) return;
                            document.getElementById('bankProofInput')?.click();
                          }}
                          onKeyDown={(e: React.KeyboardEvent) => {
                            if (isUiLocked) return;
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              document.getElementById('bankProofInput')?.click();
                            }
                          }}
                        >
                          <p className='mb-1 small fw-semibold'>Click to upload</p>
                          <p className='small text-muted mb-0'>
                            JPG, PNG, WEBP, HEIC — max 3MB
                          </p>

                          <input
                            id='bankProofInput'
                            type='file'
                            hidden
                            disabled={isUiLocked}
                            accept='image/*,.heic,.heif'
                            onChange={handleFileInputChange}
                          />
                        </div>
                      )}

                      {bankProofFile && bankProofPreview && (
                        <div className='mt-2 border rounded p-2'>
                          <div className='d-flex justify-content-between align-items-center mb-2'>
                            <span className='small fw-semibold text-truncate'>
                              {bankProofFile.name}
                            </span>
                            <Button
                              size='sm'
                              variant='outline-danger'
                              onClick={handleRemoveProof}
                            >
                              Remove
                            </Button>
                          </div>
                          <img
                            src={bankProofPreview}
                            alt='Bank Proof Preview'
                            className='img-fluid rounded'
                            style={{ maxHeight: 180 }}
                          />
                        </div>
                      )}
                    </div>
                  )}

                  {/* Submit button */}
                  <div className='mt-4'>
                    <div className='d-flex justify-content-end'>
                      {buttonConfig && (
                        <Button
                          type='button'
                          variant={buttonConfig.variant}
                          disabled={buttonConfig.disabled || isProcessing}
                          onClick={buttonConfig.onClick}
                          style={primaryBtnStyle(buttonConfig.variant)}
                          className='d-flex align-items-center justify-content-center gap-2'
                        >
                          {isProcessing ? (
                            <>
                              <Spinner size='sm' />
                              <span>Processing…</span>
                            </>
                          ) : (
                            buttonConfig.label
                          )}
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Modal lives INSIDE Formik render — reads live values */}
                  <ConfirmBankDetailsModal
                    show={showConfirmModal}
                    onClose={closeConfirmModal}
                    isSubmitting={isProcessing}
                    bankDetails={{
                      nameAsOnBankAccount: values.nameAsOnBankAccount,
                      bankAccount: values.bankAccount,
                      ifsc: values.ifsc,
                      bankName: values.bankName,
                    }}
                    onConfirm={handleConfirmAndSubmit}
                  />
                </Form>
              );
            }}
          </Formik>

          <ConfirmBankDocumentModal
            show={showUploadModal}
            onClose={closeUploadModal}
            isSubmitting={isProcessing}
            bankProofFile={bankProofFile}
            bankProofPreview={bankProofPreview}
            onConfirm={handleUploadBankProof}
          />
        </Card.Body>
      </Card>
    </>
  );
};

export default BankForm;